<template>
  <div>
    <AppDialog
      :show="show"
      class="manual-time-request-dialog"
      title="Manual time request review"
      size="medium"
      append-to-body
      :loading="loading"
      @close="closeDialog"
    >
      <div class="row">
        <div class="col-xs-12">
          <div
            v-if="collisionWarning && currentAction !== 'deny' && !noPermissionToApprove"
            class="dialog-warning red"
          >
            <i class="hi hi-warning hi-10"></i>
            <div class="small">
              {{ collisionWarning }}
            </div>
          </div>
        </div>
      </div>
      <div class="top-section">
        <div class="row">
          <div class="col-xs-8">
            <AppAvatar :avatar="manualTimeRequest.avatar" />
            {{ manualTimeRequest.name }}
          </div>
          <div class="col-xs-4 text-right">
            <span class="small text-grey text-uppercase mr-5">Status</span>
            <span :class="statusClass">{{ manualTimeRequest.status }}</span>
          </div>
        </div>
      </div>
      <div class="middle-section">
        <div class="row">
          <div class="col-xs-8">
            <div class="small text-grey text-uppercase">Timespan ({{ tzSelectedInfo.friendlyName + ' ' + tzSelectedInfo.offset }})</div>
            <div>{{ manualTimeRequest.timespan }}</div>
          </div>
          <div class="col-xs-4 text-right">
            <div class="small text-grey text-uppercase">Total</div>
            <div class="bold">{{ manualTimeRequest.duration }}</div>
            <div class="small text-grey">{{ billableString }}</div>
          </div>
        </div>
        <div class="row mt-15">
          <div class="col-xs-6">
            <div class="small text-grey text-uppercase">Project</div>
            <div>{{ manualTimeRequest.project }}</div>
            <div class="small text-grey">{{ manualTimeRequest.todo || 'No to-do' }}</div>
          </div>
          <div class="col-xs-6 text-right">
            <div class="small text-grey text-uppercase">Reason</div>
            <div class="break-word">{{ manualTimeRequest.reason }}</div>
          </div>
        </div>
        <div
          class="row mt-15"
        >
          <div class="col-xs-12">
            <TimeEntriesTimeline
              ref="timeline"
              :date="date"
              :time-entries="timeEntries"
              :selected-tz="tzSelectedInfo.name"
              :manual-time-request-project-name="manualTimeRequest.project"
              :manual-time-request-task-name="manualTimeRequest.todo"
            />
          </div>
        </div>
        <div
          v-if="showTzWarning"
          class="row mt-15"
        >
          <div class="col-xs-12">
            <TimeEntriesDifferentTimeZoneWarning
              :datetime-range="datetimeRange"
              :custom-user="user"
              :custom-user-zone="userZone"
              :custom-same-tz="sameTz"
            />
          </div>
        </div>
        <div class="row">
          <div
            v-if="manualTimeRequest.note"
            class="col-xs-12 note mt-15"
          >
            <div class="small text-grey text-uppercase">Note</div>
            <div class="break-word">
              <template v-if="isNoteTruncated && manualTimeRequest.note.length > 200">
                {{ manualTimeRequest.note.slice(0, 200) }}...
                <a
                  href="#"
                  class="show-more-less-link"
                  @click.prevent="isNoteTruncated = !isNoteTruncated"
                >show more</a>
              </template>
              <template v-else>
                {{ manualTimeRequest.note }}
                <a
                  v-if="manualTimeRequest.note.length > 200"
                  href="#"
                  class="show-more-less-link"
                  @click.prevent="isNoteTruncated = !isNoteTruncated"
                > show less</a>
              </template>
            </div>
          </div>
          <div
            v-if="reasonToDenyResponse && currentAction === 'view' && manualTimeRequest.status === 'Denied'"
            class="col-xs-12 mt-15"
          >
            <div class="small text-grey text-uppercase">Denial reason</div>
            <div class="break-word">{{ reasonToDenyResponse }}</div>
          </div>
          <div
            v-if="hasAttachment"
            class="col-xs-12 field mt-15 attachment-field"
          >
            <div class="small text-grey text-uppercase">Uploaded file</div>
            <div class="d-flex justify-content-start align-items-center">
              <div class="filename">
                {{ manualTimeRequest.attachment.metadata.filename }}
              </div>
              <a :href="manualTimeRequest.attachment_original_url">
                <i class="hi hi-export hi-12 d-flex align-items-center ml-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="limitRefreshAlert"
        class="dialog-warning orange limit-refresh-alert mt-10 mb-10"
        data-testid="limit-refresh-alert"
      >
        <i class="hi hi-warning hi-10"></i>
        <div class="small">
          {{ limitRefreshAlert }}
        </div>
      </div>
      <div
        v-if="showTimeTrackingLimits && currentAction !== 'deny'"
        :class="`time-tracking-limits ${showProjectBudgetWarning && expandTimeTrackingLimits ? 'with-project-budget-warning' : ''}`"
      >
        <div class="d-flex">
          <i
            v-if="exceedingHoursOptionSelected"
            class="hi hi-check-circle hi-12"
          ></i>
          <i
            v-else
            class="hi hi-warning hi-10 orange"
          ></i>
          <div
            class="section-heading"
          >
            {{ `Time tracking limits${exceedingHoursOptionSelected ? ':' : ''}` }}
            <span
              v-if="exceedingHoursOptionSelected"
              class="implication-value"
            >
              {{ `${exceedingHours === 'keep' ? 'Keep' : 'Discard'} exceeding hours` }}
            </span>
          </div>
          <div class="flex-grow-1 text-right">
            <i
              :class="`hi hi-caret hi-10 cursor-pointer ${expandTimeTrackingLimits ? 'rotate-180' : ''}`"
              @click="toggleExpandTimeTrackingLimits()"
            ></i>
          </div>
        </div>
        <div
          v-show="expandTimeTrackingLimits"
          class="mt-10"
        >
          {{ timeTrackingLimitsMessage }}
          <div class="d-flex mt-15">
            <div
              class="time-tracking-limits-option mr-5"
              @click="onExceedingHoursOptionSelected('discard')"
            >
              <div>
                <input
                  v-model="exceedingHours"
                  type="radio"
                  value="discard"
                  class="form-check-input"
                  data-testid="discard-exceeding-hours"
                  :disabled="!canDiscardExceedingHours"
                />
                <span :class="canDiscardExceedingHours ? '' : 'muted'">
                  Discard exceeding hours
                </span>
                <span
                  v-if="!canDiscardExceedingHours"
                  data-testid="coming-soon"
                  class="coming-soon"
                >
                  Coming soon
                </span>
              </div>
              <div class="small mt-5">
                Only approve requested time up until the limit and discard exceeding hours
              </div>
            </div>
            <div
              class="time-tracking-limits-option ml-5"
              @click="onExceedingHoursOptionSelected('keep')"
            >
              <div>
                <input
                  v-model="exceedingHours"
                  type="radio"
                  value="keep"
                  class="form-check-input"
                />
                <span>
                  Keep exceeding hours
                </span>
              </div>
              <div class="small mt-5">
                Approve requested time that went over the limit and keep exceeding hours
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="showProjectBudgetWarning && expandTimeTrackingLimits && currentAction !== 'deny'"
        :class="`project-budget-warning d-flex ${showTimeTrackingLimits ? '' : 'without-time-tracking-limits'}`"
      >
        <i class="hi hi-warning hi-10 orange"></i>
        <div class="small">
          {{ projectBudgetWarningMessage }}
        </div>
      </div>
      <div v-if="shown.team_payments && currentAction !== 'deny'">
        <div class="implication-section">
          <div class="d-flex">
            <i
              v-if="consented.team_payments"
              class="hi hi-check-circle hi-12"
            ></i>
            <i
              v-else
              class="hi hi-warning hi-10 orange"
            ></i>
            <div class="section-heading">
              {{ `Paid time overlap${consented.team_payments ? ':' : ''}` }}
              <span
                v-if="consented.team_payments"
                class="implication-value"
              >
                Consented
              </span>
            </div>
            <div class="flex-grow-1 text-right">
              <i
                :class="`hi hi-caret hi-10 cursor-pointer ${expanded.team_payments ? 'rotate-180' : ''}`"
                @click="toggle('team_payments')"
              ></i>
            </div>
          </div>
          <div
            v-show="expanded.team_payments"
            class="mt-10"
          >
            This time has already been marked as paid by the following payment(s). By approving this manual time, the member's time entry will be removed from the payment(s). There will be a discrepancy between time tracked and time marked as paid.
            <ul class="implication-list">
              <li
                v-for="(teamPayment, key) in (timeEntry.associatedRecords.team_payments)"
                :key="key"
              >
                <a
                  :href="teamPayment.url"
                  target="_blank"
                >
                  {{ formatted(teamPayment) }}
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div
          v-show="expanded.team_payments"
          class="d-flex implication-section-footer"
        >
          <input
            v-model="consented.team_payments"
            type="checkbox"
            class="checkbox"
            @click="onConsentClick('team_payments')"
          />
          <div class="small">
            I understand that approving this manual time, the member's time entry will be removed from the payment(s)
          </div>
        </div>
      </div>
      <div v-if="shown.invoices && currentAction !== 'deny'">
        <div class="implication-section">
          <div class="d-flex">
            <i
              v-if="consented.invoices"
              class="hi hi-check-circle hi-12"
            ></i>
            <i
              v-else
              class="hi hi-warning hi-10 orange"
            ></i>
            <div class="section-heading">
              {{ `Time overlap with processed invoice${consented.invoices ? ':' : ''}` }}
              <span
                v-if="consented.invoices"
                class="implication-value"
              >
                Consented
              </span>
            </div>
            <div class="flex-grow-1 text-right">
              <i
                :class="`hi hi-caret hi-10 cursor-pointer ${expanded.invoices ? 'rotate-180' : ''}`"
                @click="toggle('invoices')"
              ></i>
            </div>
          </div>
          <div
            v-show="expanded.invoices"
            class="mt-10"
          >
            This time has been invoiced by the following invoice(s):
            <ul class="implication-list">
              <li
                v-for="(invoice, key) in (timeEntry.associatedRecords.invoices)"
                :key="key"
              >
                <a
                  :href="invoice.url"
                  target="_blank"
                >
                  {{ invoice.number }}
                </a>
              </li>
            </ul>
          </div>
          <div
            v-show="expanded.invoices"
            class="mt-10"
          >
            This time has been user invoiced by the following invoice(s):
            <ul class="implication-list">
              <li
                v-for="(userInvoice, key) in (timeEntry.associatedRecords.user_invoices)"
                :key="key"
              >
                <a
                  :href="userInvoice.url"
                  target="_blank"
                >
                  {{ userInvoice.number }}
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div
          v-show="expanded.invoices"
          class="d-flex implication-section-footer"
        >
          <input
            v-model="consented.invoices"
            type="checkbox"
            class="checkbox"
            @click="onConsentClick('invoices')"
          />
          <div class="small">
            I understand that by editing or removing this time will result in out of sync invoices
          </div>
        </div>
      </div>
      <div v-if="shown.integrations && currentAction !== 'deny'">
        <div class="implication-section">
          <div class="d-flex">
            <i
              v-if="consented.integrations"
              class="hi hi-check-circle hi-12"
            ></i>
            <i
              v-else
              class="hi hi-warning hi-10 orange"
            ></i>
            <div class="section-heading">
              {{ `Integration task out of sync${consented.integrations ? ':' : ''}` }}
              <span
                v-if="consented.integrations"
                class="implication-value"
              >
                Consented
              </span>
            </div>
            <div class="flex-grow-1 text-right">
              <i
                :class="`hi hi-caret hi-10 cursor-pointer ${expanded.integrations ? 'rotate-180' : ''}`"
                @click="toggle('integrations')"
              ></i>
            </div>
          </div>
          <div
            v-show="expanded.integrations"
            class="mt-10"
          >
            Time has already been synced to the following integration(s) for this day:
            <ul class="implication-list">
              <li
                v-for="(integration, key) in (timeEntry.associatedRecords.integrations)"
                :key="key"
              >
                <a
                  :href="integration.url"
                  target="_blank"
                >
                  {{ integration.name }}
                </a>
              </li>
            </ul>
            By approving this manual time, the time won't be automatically updated in the integration(s). To avoid a discrepancy, add this time in the integration(s).
          </div>
        </div>
        <div
          v-show="expanded.integrations"
          class="d-flex implication-section-footer"
        >
          <input
            v-model="consented.integrations"
            type="checkbox"
            class="checkbox"
            @click="onConsentClick('integrations')"
          />
          <div class="small">
            I understand that by editing this time will result in out of sync time with the integration
          </div>
        </div>
      </div>
      <div class="row">
        <div
          v-if="currentAction === 'deny' && manualTimeRequest.status === 'Submitted'"
          class="col-xs-12 mt-15"
        >
          <ValidationObserver ref="observer">
            <ValidationProvider
              v-slot="{ errors }"
              mode="aggressive"
              rules="required|max:200"
              name="reason for denying"
            >
              <div :class="{ 'has-error': errors[0] }">
                <div class="small text-grey text-uppercase mb-5">Reason for denying*</div>
                <textarea
                  v-model="reasonToDeny"
                  class="form-control"
                  placeholder="Enter the reason for denying"
                />
                <span class="help-block mb-15">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </ValidationObserver>
        </div>
      </div>

      <template slot="footer">
        <AppButton
          v-show="showCancelButton"
          type="default"
          @click="closeDialog"
        >
          Cancel
        </AppButton>
        <AppButton
          v-show="showDenyButton"
          v-tooltip
          :title="noPermissionToDeny ? denyPermissionMessage : ''"
          :disabled="!allowDeny"
          type="danger"
          @click="deny"
        >
          Deny
        </AppButton>
        <AppButton
          v-show="showApproveButton"
          v-tooltip
          type="primary"
          :title="noPermissionToApprove ? approvePermissionMessage : ''"
          :disabled="validating || !allowApprove"
          @click="approve({calledByApproveBtn: true})"
        >
          {{ validating ? 'Validating' : 'Approve' }}
        </AppButton>
      </template>
    </AppDialog>
    <AppConfirm
      v-if="showIncreaseLimitDialog"
      title="Confirm changes"
      confirm-label="OK"
      @cancel="cancelIncreaseLimit"
      @confirm="increaseLimitAndSave"
    >
      {{ confirmationMessage }}
    </AppConfirm>
    <AppConfirm
      v-if="showProjectBudgetConfirm"
      title="Confirm changes"
      @cancel="cancelIncreaseBudget"
      @confirm="increaseBudgetAndSave"
    >
      {{ projectBudgetWarningMessage }}
    </AppConfirm>
    <TimeEntriesOverrideDialog
      :show.sync="showOverrideDialog"
      type="add"
      :time-entry="timeEntry"
      @submit="approve"
      @close="closeOverrideDialog"
    />
  </div>
</template>

<script>
import TimeEntriesTimeline from '~/components/time_entries/TimeEntriesTimeline.vue';
import TimeEntriesOverrideDialog from '~/components/time_entries/TimeEntriesOverrideDialog.vue';
import TimeEntriesDifferentTimeZoneWarning from '~/components/time_entries/TimeEntriesDifferentTimeZoneWarning.vue';
import { ManualTimeRequestsResource, TimeEntriesResource } from '~/resources';
import { formatDatetimeRange, formatHours } from '~/utils/time_utils';
import { dispatchPendingActionCountsRefresh } from '~/../components/sidebar_component/x-sidebar_component';
import moment from 'moment';

const LIMIT_REFRESH_ALERT = 'The time limit calculation changed or expired while this dialog was open. We refreshed it. Please review the updated limits and choose how to proceed again.';

export default {
  name: 'ManualTimeRequestDialog',
  components: { TimeEntriesTimeline, TimeEntriesOverrideDialog, TimeEntriesDifferentTimeZoneWarning },
  props: {
    action: {
      type: String,
      required: true,
    },
    organizationId: {
      type: Number,
      required: true,
    },
    requestId: {
      type: String,
      required: true,
    },
    timeZone: {
      type: String,
      default: 'mine'
    },
    isZone: {
      type: Boolean,
      default: false,
    },
    canDiscardExceedingHours: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentAction: this.action,
      show: true,
      loading: true,
      noPermissionToApprove: false,
      noPermissionToDeny: false,
      approvePermissionMessage: '',
      denyPermissionMessage: '',
      collisionOverlap: 0,
      allowApprove: true,
      allowDeny: true,
      validationToken: '',
      initialValidationToken: '',
      confirmationMessage: '',
      showIncreaseLimitDialog: false,
      limits: null,
      addTime: null,
      limitConfirmationToken: null,
      increaseLimit: false,
      showProjectBudgetConfirm: false,
      projectBudgetWarningMessage: '',
      increaseBudget: false,
      showOverrideDialog: false,
      reasonToDeny: '',
      collisionWarning: '',
      showTimeTrackingLimits: false,
      expandTimeTrackingLimits: true,
      exceedingHours: '',
      exceedingHoursOptionSelected: false,
      limitRefreshAlert: '',
      showProjectBudgetWarning: false,
      shown: {},
      expanded: {},
      consented: {},
      sectionKeys: [],
      validating: false,
      checkRequestLoading: false,
      manualTimeRequest: { status: '' },
      datetimeRange: {
        start: null,
        stop: null
      },
      user: {
        name: null
      },
      userZone: {
        name: null
      },
      sameTz: true,
      tzSelectedInfo: {
        name: 'UTC',
        offset: '-03:00',
        friendlyName: 'UTC',
      },
      showTzWarning: false,
      date: moment(),
      timeEntry: {},
      timeEntryId: '',
      timeEntries: [],
      canApprove: false,
      canDeny: false,
      isNoteTruncated: true,
      reasonToDenyResponse: '',
      extraSeconds: 0,
      discardClicked: false,
    };
  },
  computed: {
    statusClass() {
      return (
        'label label-' +
        { submitted: 'info', approved: 'success', denied: 'danger' }[this.manualTimeRequest.status.toLowerCase()]
      );
    },
    billableString() {
      return this.manualTimeRequest.billable ? 'Billable' : 'Non-billable';
    },
    showCancelButton() {
      return ['deny', 'approve'].includes(this.currentAction) && this.manualTimeRequest.status === 'Submitted';
    },
    showDenyButton() {
      return ['deny', 'view'].includes(this.currentAction) && this.manualTimeRequest.status === 'Submitted';
    },
    showApproveButton() {
      return ['approve', 'view'].includes(this.currentAction) && this.manualTimeRequest.status === 'Submitted';
    },
    hasAttachment() {
      if (this.manualTimeRequest.attachment) {
        return Object.keys(this.manualTimeRequest.attachment).length !== 0;
      }

      return false;
    },
    getProjectTaskName() {
      const projectName = this.manualTimeRequest.project;
      const taskName = this.manualTimeRequest.todo;

      if (taskName) {
        return `Project: ${projectName} - Task: ${taskName}`;
      }

      return `Project: ${projectName}`;
    },
    timeTrackingLimitsMessage() {
      const limitsDetails = this.limits.map((limit) => {
        const allowed = formatHours(parseFloat(limit.allowed_hours));
        const extra = formatHours(parseFloat(limit.extra_hours));

        return `${limit.limit_type} limit (${allowed}) by ${extra}`;
      }).join(' and ');

      return `Approving this request will exceed ${this.manualTimeRequest.name}’s ${limitsDetails}. Choose how you would like to proceed.`;
    },
  },
  created() {
    if (window.location.search && window.location.search.includes('manual_time_request_id')) {
      const urlParams = new URLSearchParams(window.location.search);

      urlParams.delete('manual_time_request_id');
      const newUrl = urlParams.toString()
        ? `${window.location.pathname}?${urlParams.toString()}`
        : window.location.pathname;

      window.history.replaceState({}, '', newUrl);
    }
    this.loadData();
  },
  methods: {
    loadData() {
      this.loading = true;

      ManualTimeRequestsResource.show({ organization_id: this.organizationId, id: this.requestId }, { time_zone: this.timeZone }).then((data) => {
        const parsedStartTime = moment.parseZone(data.manual_time_request.start_time_in_selected_timezone);
        const parsedStopTime = moment.parseZone(data.manual_time_request.stop_time_in_selected_timezone);
        const timeEntry = Object.assign(data.time_entry, {
          start_time: parsedStartTime,
          stop_time: parsedStopTime,
        });

        this.manualTimeRequest = data.manual_time_request;
        this.date = moment(parsedStartTime.format('YYYY-MM-DD'), 'YYYY-MM-DD');
        this.timeEntry = data.time_entry;
        this.timeEntryId = data.time_entry_id;
        this.timeEntries = [timeEntry];
        this.canApprove = data.can_approve;
        this.reasonToDenyResponse = data.manual_time_request.reason_to_deny;
        this.canDeny = data.can_deny;
        this.approvePermissionMessage = data.approve_permission_message;
        this.denyPermissionMessage = data.deny_permission_message;
        this.datetimeRange = {
          start: parsedStartTime,
          stop: parsedStopTime,
        };
        this.user = { name: data.manual_time_request.name };
        this.userZone = { name: data.manual_time_request.timezone };
        this.sameTz = data.manual_time_request.timezone === data.tz_selected_info.name;
        this.tzSelectedInfo = {
          name: data.tz_selected_info.name,
          offset: data.tz_selected_info.offset,
          friendlyName: data.tz_selected_info.friendly_name,
        };
        this.showTzWarning = !this.sameTz;

        if (this.manualTimeRequest.status !== 'Submitted') {
          this.loading = false;

          return;
        }

        if (!this.canApprove) {
          this.allowApprove = false;
          this.noPermissionToApprove = true;
        }

        if (!this.canDeny) {
          this.allowDeny = false;
          this.noPermissionToDeny = true;
        }

        TimeEntriesResource.check(
          { id: this.timeEntryId },
          {
            manual_time_request_id: this.manualTimeRequest.id,
            organization_id: this.organizationId,
            source_organization_id: this.organizationId,
            needs_html: false,
          },
        ).then((response) => {
          if (response.allowed) {
            this.validationToken = response.validation_token;
            this.initialValidationToken = response.validation_token;
          } else {
            this.allowApprove = false;
          }

          if (Object.keys(response.not_accessible).length > 0) {
            this.allowApprove = false;
            this.collisionWarning =
              'This manual time overlaps with tracked time in a project that is either inaccessible or deleted. You cannot approve this time.';
          } else if (response.immutable && !response.allowed) {
            this.allowApprove = false;
            this.collisionWarning = 'This manual time overlaps with tracked time that has been paid, invoiced or logged to a external integration. You do not have permission to approve this request. Contact your Organization owner to approve this time.';
          } else if (Object.keys(response.collisions).length > 0) {
            const tracked = (collision) => collision[1].tracked;

            this.collisionOverlap = Math.round(
              Object.entries(response.collisions).reduce((sum, collision) => sum + tracked(collision), 0) / 60,
            );

            if (response.work_break) {
              this.allowApprove = false;
              this.collisionWarning = `This manual time overlaps with a work break. You cannot approve this time.`;
            } else {
              const currentProjectId = this.manualTimeRequest.project_id;
              const currentTaskId = this.manualTimeRequest.task_id;
              const hasDifferentProjectOrTask = Object.entries(response.collisions).some(([key, collision]) => {
                const [collisionProjectId, collisionTaskId] = key.split(':').map(id => id === '' ? null : Number(id));

                return collisionProjectId !== currentProjectId ||
                      (collisionProjectId === currentProjectId && collisionTaskId !== currentTaskId);
              });

              if (hasDifferentProjectOrTask) {
                this.collisionWarning = `This manual time entry overlaps with tracked time by ${this.collisionOverlap} minutes on other projects, tasks or work orders. By approving this manual time request, the overlapping time will be reassigned to ${this.getProjectTaskName}.`;
              } else {
                this.collisionWarning = `The manual time overlaps with tracked time by ${this.collisionOverlap} minutes. By approving this time, the overlapping time will not be saved.`;
              }
            }
          }
        }).catch((_error) => {
          this.handleError();
        }).finally(() => {
          this.loading = false;
        });

        this.$store.dispatch('setSelectedTimeEntry', { timeEntry: this.timeEntry });
      }).catch((_error => {
        this.handleError();
      }));
    },
    handleError() {
      this.closeDialog();
      this.loading = false;
      this.$appMessage('Unexpected error. Please try again.');
    },
    closeDialog() {
      this.show = false;
    },
    async deny() {
      if (this.currentAction === 'view') {
        this.loading = true;

        setTimeout(() => {
          this.currentAction = 'deny';
          this.loading = false;
        }, 500);

        return null;
      }

      const isValid = await this.$refs.observer.validate();

      if (!isValid) {
        return;
      }

      if (!this.allowDeny) {
        return;
      }

      ManualTimeRequestsResource.deny(
        { organization_id: this.organizationId, id: this.manualTimeRequest.id },
        { reason_to_deny: this.reasonToDeny },
      )
        .then((response) => {
          if (response.success) {
            dispatchPendingActionCountsRefresh();
            this.show = false;
            if (this.isZone) {
              document.getElementById('manual_time_requests_list_form').requestSubmit()
            } else {
              window.location.reload();
            }
          } else if (response.error) {
            this.$appMessage(response.error);
          }
        })
        .catch((_error) => {
          this.$appMessage('Unexpected error. Please try again.');
          setTimeout(function () {
            // when the backend returns a 403 error, the frontend reloads the page and the user doesn't have time to read the notification
          }, 2000);
        });
    },
    approve({exceedingHoursOptionSelected = false, calledByApproveBtn = false} = {}) {
      const increaseBudget = exceedingHoursOptionSelected ? false : this.increaseBudget;
      const attemptedLimitIncrease = this.increaseLimit && this.limitConfirmationToken;

      this.validating = true;
      const limitConfirmationParams = this.increaseLimit
        ? {
            limits: this.limits,
            add_time: this.addTime,
            limit_confirmation_token: this.limitConfirmationToken,
          }
        : {};

      TimeEntriesResource.approveManualTimeRequest(
        {},
        {
          manual_time_request_id: this.manualTimeRequest.id,
          validation_token: this.validationToken,
          organization_id: this.organizationId,
          increase_limit: this.increaseLimit,
          allow_above_project_budget: increaseBudget,
          extra_seconds: calledByApproveBtn ? this.extraSeconds : 0,
          ...limitConfirmationParams,
        },
      )
        .then((response) => {
          if (response.success) {
            dispatchPendingActionCountsRefresh();
            this.show = false;
            if (this.isZone) {
              document.getElementById('manual_time_requests_list_form').requestSubmit()
            } else {
              window.location.reload();
            }
          } else if (response.action === 'limit-confirmation-stale') {
            this.resetLimitChoiceForRefresh(response.alert);
            this.approve();
          } else if (response.action === 'confirmation-required') {
            if (response.limit_exceeded) {
              if (attemptedLimitIncrease) {
                this.resetLimitChoiceForRefresh();
              }
              this.showTimeTrackingLimits = true;
              this.limits = response.limit_exceeded.limits;
              this.addTime = response.limit_exceeded.add_time;
              this.limitConfirmationToken = response.limit_exceeded.limit_confirmation_token;

              if (!this.exceedingHoursOptionSelected) {
                this.allowApprove = false;
              }
            }
            if (response.project_limit_reached) {
              this.showProjectBudgetWarning = true;
              this.projectBudgetWarningMessage = response.project_limit_reached.message;
              this.increaseBudget = true;
            } else {
              this.showProjectBudgetWarning = false;
              this.projectBudgetWarningMessage = '';

              if (this.canDiscardExceedingHours) {
                this.increaseBudget = false;
              }
            }
            if (response.collisions) {
              this.$store.dispatch('assignAssociatedRecords', {
                id: this.timeEntry.id,
                associatedRecords: response.collisions.associated_records,
              });
              this.validationToken = response.collisions.validation_token;
              this.allowApprove = false;
              const associatedRecordKeys = Object.keys(this.timeEntry.associatedRecords);
              const sortedKeys = new Set();

              ['team_payments', 'invoices', 'user_invoices', 'integrations'].forEach((key) => {
                if (associatedRecordKeys.includes(key)) {
                  if (key === 'user_invoices') {
                    sortedKeys.add('invoices');
                  } else {
                    sortedKeys.add(key);
                  }
                }
              });
              this.sectionKeys = Array.from(sortedKeys);

              this.expanded = Object.fromEntries(
                this.sectionKeys.map((key) => [key, this.canDiscardExceedingHours])
              );
              this.consented = Object.fromEntries(
                this.sectionKeys.map((key) => [key, false])
              );
              this.shown = Object.fromEntries(
                this.sectionKeys.map((key) => [key, true])
              );

              if (this.showTimeTrackingLimits && !this.canDiscardExceedingHours) {
                this.expandTimeTrackingLimits = false;
              }
              this.expanded[this.sectionKeys[0]] = true;
            } else {
              this.shown = {};
            }
          } else if (response.action === 'error-response') {
            this.$appMessage(response.alert);
          } else {
            this.$appMessage('Unexpected error. Please try again.');
          }
        })
        .catch((_error) => {
          this.$appMessage('Unexpected error. Please try again.');
        })
        .finally(() => {
          this.validating = false;
        });
    },
    increaseLimitAndSave() {
      this.increaseLimit = true;
      this.approve();
      this.showIncreaseLimitDialog = false;
    },
    increaseBudgetAndSave() {
      this.increaseBudget = true;
      this.approve();
      this.showProjectBudgetConfirm = false;
    },
    cancelIncreaseLimit() {
      this.closeOverrideDialog();
      this.showIncreaseLimitDialog = false;
    },
    cancelIncreaseBudget() {
      this.closeOverrideDialog();
      this.showProjectBudgetConfirm = false;
    },
    closeOverrideDialog() {
      this.increaseBudget = false;
      this.increaseLimit = false;
    },
    resetLimitChoiceForRefresh(alert = LIMIT_REFRESH_ALERT) {
      this.increaseLimit = false;
      this.increaseBudget = false;
      this.limitConfirmationToken = null;
      this.exceedingHours = '';
      this.exceedingHoursOptionSelected = false;
      this.extraSeconds = 0;
      this.discardClicked = false;
      this.allowApprove = false;
      this.limitRefreshAlert = alert;
      this.$appMessage(alert);
    },
    toggleExpandTimeTrackingLimits() {
      this.expandTimeTrackingLimits = !this.expandTimeTrackingLimits;
    },
    toggle(key) {
      this.expanded[key] = !this.expanded[key];
    },
    onExceedingHoursOptionSelected(optionName) {
      if (!this.canDiscardExceedingHours) {
        if (optionName === 'discard') {
          return false;
        }
        this.allowApprove = true;
      }

      this.exceedingHours = optionName;
      this.exceedingHoursOptionSelected = true;

      if (optionName === 'discard') {
        this.extraSeconds = Math.max(...this.limits.map((l) => Number(l.extra_seconds)));
        this.discardClicked = true;
      } else {
        this.extraSeconds = 0;
        if (this.canDiscardExceedingHours) {
          this.increaseBudget = true;
        }
      }

      if (this.discardClicked) {
        this.increaseLimit = false;
        this.validationToken = this.initialValidationToken;
        this.approve({exceedingHoursOptionSelected: this.exceedingHoursOptionSelected});
      }

      this.increaseLimit = optionName === 'keep';

      if (!this.sectionKeys.length) {
        this.allowApprove = true;
      }
    },
    formatted(teamPayment) {
      return `Payment for ${formatDatetimeRange(moment(teamPayment.start), moment(teamPayment.stop), { style: 'date' })}`;
    },
    onConsentClick(key) {
      this.consented[key] = !this.consented[key];

      const allConsented = Object.values(this.consented).every(Boolean);
      const requiresLimitChoice = this.showTimeTrackingLimits && this.canDiscardExceedingHours;

      this.allowApprove = (!requiresLimitChoice || this.exceedingHoursOptionSelected) && allConsented;

      if (event.target.checked) {
        this.expanded[key] = false;

        let index = this.sectionKeys.indexOf(key) + 1;

        while (index < this.sectionKeys.length) {
          if (this.consented[this.sectionKeys[index]] === false) {
            setTimeout(() => {
              this.expanded[this.sectionKeys[index]] = true;
            }, 500);

            break;
          } else {
            ++index;
          }
        }
      }
    },
  },
};
</script>
